import { Link } from 'react-router-dom'

import {
  DropdownMenu,
  IconButton,
  EllipsisVerticalIcon,
  GlobeIcon,
  useIsMediumPageHeader,
} from '@buffer-mono/popcorn'
import type { ButtonProps } from '@buffer-mono/popcorn'

import { ChannelDeveloperDataMenuItems } from '~publish/components/DeveloperData'
import type { FilterablePostType } from '~publish/helpers/postType'
import { useTagsQuery } from '~publish/hooks/useTags'
import { channelSettings } from '~publish/legacy/routes'
import { TypeFilterSubMenu } from '~publish/pages/Calendar/CalendarHeader/TypeFilterSubMenu'
import { isViewOnlyPublishChannel } from '~publish/utils/permissions/viewOnlyPublish'

import { useSelectedChannel } from './ChannelContext'
import { PostTypeFilterSubMenu } from './PostTypeFilterSubMenu'
import { TagFilterSubMenu } from './TagFilterSubMenu'
import { useSlotsEnabled } from '../../hooks/useSlotsEnabled'

import styles from './MoreActions.module.css'

import type { JSX } from 'react'

interface MoreActionsProps extends Omit<ButtonProps, 'children'> {
  channelId: string
  selectedTags?: string[]
  onTagSelect?: (tagIds: string[]) => void
  /**
   * Post formats this channel's service can be filtered by. Empty for a
   * service the filter does not support, which hides the submenu.
   * @see supportedPostTypesForService
   */
  postTypeOptions?: readonly FilterablePostType[]
  selectedPostTypes?: string[]
  onPostTypeSelect?: (postTypes: string[]) => void
  timezone?: string
  /**
   * Calendar's status preset filter (All Posts / Drafts / Scheduled / Sent).
   * Unrelated to `postTypeOptions`, which filters by post format.
   */
  showTypeFilter?: boolean
  triggerIcon?: React.ReactElement
}

export function ShowPostingTimesMenuItem(): JSX.Element {
  const [showPostingTimes, setShowPostingTimes] = useSlotsEnabled()

  return (
    <DropdownMenu.CheckboxItem
      className={styles.item}
      checked={showPostingTimes}
      onCheckedChange={(checked): void => {
        if (typeof checked === 'boolean') {
          setShowPostingTimes(checked)
        }
      }}
      onSelect={(event): void => event.preventDefault()}
    >
      Show Posting Times
    </DropdownMenu.CheckboxItem>
  )
}

export function MoreActions({
  channelId,
  selectedTags = [],
  onTagSelect,
  postTypeOptions = [],
  selectedPostTypes = [],
  onPostTypeSelect,
  timezone,
  showTypeFilter,
  triggerIcon,
  ...props
}: MoreActionsProps): JSX.Element | null {
  const { tags } = useTagsQuery()
  const channel = useSelectedChannel()
  // Use JS conditional rendering: the dropdown content is portaled out of the
  // page-header container, so CSS container queries can't reach it.
  const isMediumHeader = useIsMediumPageHeader()

  // With posting times and the timezone link hidden for view-only access,
  // the wide-header menu has nothing user-facing left, so the trigger is
  // dropped entirely.
  const isViewOnly = isViewOnlyPublishChannel(channel)
  const showPostingTimeItems = !isViewOnly

  const showMobileTypeFilter = isMediumHeader && showTypeFilter
  const showMobileFilters = isMediumHeader && tags.length > 0 && onTagSelect
  const showMobilePostTypeFilter =
    isMediumHeader && postTypeOptions.length > 0 && onPostTypeSelect
  const showMobileTimezone = isMediumHeader && timezone && !isViewOnly
  if (
    !showPostingTimeItems &&
    !showMobileTypeFilter &&
    !showMobileFilters &&
    !showMobilePostTypeFilter &&
    !showMobileTimezone
  ) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <IconButton variant="tertiary" label="More actions" {...props}>
          {triggerIcon ?? <EllipsisVerticalIcon />}
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        {showMobileTypeFilter && (
          <>
            <TypeFilterSubMenu />
            <DropdownMenu.Separator />
          </>
        )}

        {showMobileFilters && (
          <>
            <TagFilterSubMenu
              selectedTags={selectedTags}
              onTagSelect={onTagSelect}
            />
            <DropdownMenu.Separator />
          </>
        )}

        {showMobilePostTypeFilter && (
          <>
            <PostTypeFilterSubMenu
              options={postTypeOptions}
              selectedPostTypes={selectedPostTypes}
              onPostTypeSelect={onPostTypeSelect}
            />
            <DropdownMenu.Separator />
          </>
        )}

        {showMobileTimezone && (
          <>
            <DropdownMenu.Item asChild>
              <Link
                to={channelSettings.getRoute({
                  channelId,
                  tab: 'posting-schedule',
                })}
                className={styles.link}
              >
                <GlobeIcon />
                {timezone.split('/').pop()?.replace(/_/g, ' ')}
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
          </>
        )}

        {showPostingTimeItems && (
          <>
            <ShowPostingTimesMenuItem />
            <DropdownMenu.Item asChild>
              <Link
                to={channelSettings.getRoute({
                  channelId,
                  tab: 'posting-schedule',
                })}
                className={styles.link}
              >
                Manage Posting Times
              </Link>
            </DropdownMenu.Item>
          </>
        )}
        <ChannelDeveloperDataMenuItems
          channelId={channelId}
          serviceId={channel.serviceId}
          showBackfillAnalytics
          withSeparator
        />
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
